import { GravityDrop, MaskRemoval } from "@slot/presentation";
import type { GridLayer, GridModel, MaskRemovalOptions, PreparedDropPlan, PresentationTaskRunner, SymbolLandEffect, SymbolRemovalEffect, SymbolRemovalBurstSpawner, TweenEasing } from "@slot/presentation";

export interface TumbleMechanicDeps {
  /** Optional only for headless tests: without a runner removals are instant. */
  taskRunner?: PresentationTaskRunner | null;
  /** A land effect like the reel mechanic's (a symbol effect the game owns); null → a tumbled symbol lands plain. */
  landEffect?: SymbolLandEffect | null;
  /** The burst played on each removed cell; null → plain shrink. */
  removalEffectSpawner?: SymbolRemovalBurstSpawner | null;
  /** Per-symbol removal effect; null → every cell shrinks + bursts. */
  removalEffect?: SymbolRemovalEffect | null;
}

/**
 * The tumble mechanic over a `GridLayer`: masked cells shrink out, survivors fall, fresh symbols
 * drop in from above (plan step 5, pass 3).
 */
export class TumbleMechanic {
  /** The tumble's own scope — independent of the reel spin's "reel_motion". */
  public static readonly SCOPE = "reel_tumble_motion";

  private readonly grid: GridLayer;
  private readonly taskRunner: PresentationTaskRunner | null;
  private readonly landEffect: SymbolLandEffect | null;
  private readonly removal: MaskRemoval;
  private dropInstance: GravityDrop | null = null;

  /** Built on first use, over the grid's pool + layout (as the reel mechanic builds its drum). */
  private get drop(): GravityDrop {
    if (!this.dropInstance) {
      this.dropInstance = new GravityDrop({
        cells: this.grid,
        pool: this.grid.pool,
        layoutUtil: this.grid.layoutUtil,
        scope: TumbleMechanic.SCOPE,
      });
    }
    return this.dropInstance;
  }

  public constructor(grid: GridLayer, deps: TumbleMechanicDeps) {
    this.grid = grid;
    this.taskRunner = deps.taskRunner ?? null;
    this.landEffect = deps.landEffect ?? null;
    this.removal = new MaskRemoval({
      grid,
      scope: TumbleMechanic.SCOPE,
      taskRunner: this.taskRunner,
      burstSpawner: deps.removalEffectSpawner ?? null,
      symbolEffect: deps.removalEffect ?? null,
      releaseReason: "tumble_remove_by_mask",
    });
  }

  /** The masked (winning) cells leave the grid (see `MaskRemoval`); the collapse event then drives the drop. */
  public removeByMask(mask: string, options: MaskRemovalOptions): Promise<void> {
    return this.removal.removeByMask(mask, options);
  }

  // ---------------------------------------------------------------------------
  // Tumble — survivors fall + new symbols drop in (prepare / animate split)
  // ---------------------------------------------------------------------------

  /**
   * Plan the tumble against the removal mask + next grid. Re-maps the grid's cells
   * to the post-tumble mapping immediately. If there is no prior rendered grid
   * (defensive — a collapse with no preceding spin), renders the target directly
   * and returns an empty plan.
   */
  public prepareTumbleToModel(input: {
    removedPositionMask: string;
    nextModel: GridModel;
    fallDurationSeconds?: number;
    spawnDurationSeconds?: number;
    columnStaggerSeconds?: number;
    withinColumnStaggerSeconds?: number;
    strictSymbolConsistencyCheck?: boolean;
  }): PreparedDropPlan {
    this.grid.ensureLayout();

    if (!this.grid.lastModel) {
      this.grid.renderGridModel(input.nextModel);
      return GravityDrop.instantPlan(input.nextModel, this.grid.cellSnapshot(), input);
    }

    return this.drop.prepareDropToModel({
      removedPositionMask: input.removedPositionMask,
      nextModel: input.nextModel,
      gridColumnCount: this.grid.gridColumnCount,
      gridRowCount: this.grid.gridRowCount,
      layout: this.grid.getComputedLayout(),
      fallDurationSeconds: input.fallDurationSeconds,
      spawnDurationSeconds: input.spawnDurationSeconds,
      columnStaggerSeconds: input.columnStaggerSeconds,
      withinColumnStaggerSeconds: input.withinColumnStaggerSeconds,
      strictSymbolConsistencyCheck: input.strictSymbolConsistencyCheck,
    });
  }

  /**
   * Animate a prepared tumble plan, then make its model authoritative. Scatter
   * drops during a tumble chain don't re-fire the landing stinger (reserved for
   * the initial reveal) — same rule cascade applies.
   */
  public async animatePreparedTumble(
    plan: PreparedDropPlan,
    options?: {
      enableOvershoot?: boolean;
      overshootPixels?: number;
      overshootPhaseRatio?: number;
      overshootSettleEasing?: TweenEasing;
      dropSpeedPixelsPerSecond?: number;
      minDropDurationSeconds?: number;
      maxDropDurationSeconds?: number;
    },
  ): Promise<void> {
    // A fresh sequence per tumble step (the pitched stingers restart).
    this.grid.notifySequenceStart();

    await this.drop.animatePreparedDrop({
      plan,
      runner: this.taskRunner,
      gridColumnCount: this.grid.gridColumnCount,
      gridRowCount: this.grid.gridRowCount,
      layout: this.grid.getComputedLayout(),
      enableOvershoot: options?.enableOvershoot,
      overshootPixels: options?.overshootPixels,
      overshootPhaseRatio: options?.overshootPhaseRatio,
      overshootSettleEasing: options?.overshootSettleEasing,
      dropSpeedPixelsPerSecond: options?.dropSpeedPixelsPerSecond,
      minDropDurationSeconds: options?.minDropDurationSeconds,
      maxDropDurationSeconds: options?.maxDropDurationSeconds,
      onColumnDropLanded: (column: number) => {
        this.grid.notifyColumnLanded(column, { isChainDrop: true });
        this.playTumbleLandAnimationForColumn(column);
      },
      strictVerifyIdAtIndex: (index: number) => this.grid.getSymbolViewAtIndex(index)?.getSymbolId() ?? null,
    });

    this.grid.commitModel(plan.nextModel);
  }

  /**
   * Play the per-symbol LAND animation for every cell in a just-dropped column, on the view the
   * grid already holds there (`prepareTumbleToModel` bound it before the fall), as the spin's
   * landing does. Without this, symbols that arrive by TUMBLING never play their land animation at
   * all, while symbols that arrive by SPINNING do.
   */
  private playTumbleLandAnimationForColumn(column: number): void {
    const effect = this.landEffect;
    if (!effect) return;
    for (let row = 0; row < this.grid.gridRowCount; row++) {
      const index = row * this.grid.gridColumnCount + column;
      const view = this.grid.getSymbolViewAtIndex(index);
      const symbolId = view?.getSymbolId() ?? "";
      if (!view || !symbolId) continue;
      effect.play({ symbolId, cellNode: view.contentNode ?? view.node, symbolSprite: view.symbolSprite, index });
    }
  }

  /** Convenience: prepare + animate in one call (used when no prepare/present split is needed). */
  public async tumbleToModel(input: {
    removedPositionMask: string;
    nextModel: GridModel;
    fallDurationSeconds?: number;
    spawnDurationSeconds?: number;
    columnStaggerSeconds?: number;
    withinColumnStaggerSeconds?: number;
    strictSymbolConsistencyCheck?: boolean;
    enableOvershoot?: boolean;
    overshootPixels?: number;
    overshootPhaseRatio?: number;
    overshootSettleEasing?: TweenEasing;
    dropSpeedPixelsPerSecond?: number;
    minDropDurationSeconds?: number;
    maxDropDurationSeconds?: number;
  }): Promise<void> {
    const plan = this.prepareTumbleToModel(input);
    await this.animatePreparedTumble(plan, input);
  }
}
